import { Injectable } from '@angular/core';
import moment from 'moment';
import ms from 'ms';
import { environment } from 'src/environments/environment';
import { IShift } from '../../../interfaces/shift';
import { ShiftCollectionService } from '../shift-collection/shift-collection.service';
import { Parameter } from 'projects/common/src/app/classes';
import { CacheService, NotificationHandlerService, ParameterHandlerService } from 'projects/common/src/app/services';
import { IError } from 'projects/common/src/app/interfaces';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { padNumber } from 'projects/common/src/app/services/utils/utilities';

@Injectable({
  providedIn: 'root',
})
export class ShiftService extends Parameter<IShift> {
  protected get target(): IShift {
    return this.cacheService.session(this.name);
  }

  protected set target(value: IShift) {
    this.cacheService.saveSession(this.name, value);
    // notificar a los suscriptores sobre el cambio de valor.
    this.sourceSubject.next(value);
  }

  private collection: IShift[];
  private readonly DEFAULT_SHIFT: IShift = {
    shiftId: -1,
    name: '',
  };
  private timeoutToken: any;

  constructor(
    private cacheService: CacheService,
    protected http: HttpClient,
    protected notificationService: NotificationHandlerService,
    private shiftsService: ShiftCollectionService,
    private parameterService: ParameterHandlerService,
  ) {
    super();
    this.name = 'shift';
    this.shiftsService.getObservable().subscribe((shifts: IShift[]) => {
      this.collection = shifts;
    });
  }

  public start(): Promise<IShift> {
    return new Promise((resolve) => {
      setTimeout(() => {
        const shiftCollection = this.shiftsService.get();
        if (shiftCollection) {
          const currentShift = this.currentShift();
          this.set(currentShift);
          const timeForNextShift = this.timeForNextShift();
          this.info(`Next shift initialize in ${ms(timeForNextShift, { long: true })}`);
          this.timeoutToken = setTimeout(() => {
            this.set(this.currentShift());
            this.start();
          }, timeForNextShift);
          resolve(currentShift);
        } else {
          this.error('No shift provided yet.');
          resolve(null);
        }
      }, 3000);
    });
  }

  public startWhen(source: Observable<IShift[]>): void {
    this.subscription = source.pipe(filter((data) => !!data)).subscribe((data) => {
      this.alreadyStarted = true;
      clearTimeout(this.timeoutToken);
      this.init();
    });
  }

  /**
   * Retorna colección de turnos actual
   */
  public getShiftCollection(): IShift[] {
    if (!this.collection) {
      if (this.parameterService.parameters.containsKey('shift-collection')) {
        return this.parameterService.parameters.item('shift-collection').value;
      }
      return null;
    }
    return this.collection;
  }

  /**
   * Retorna un turno contenido en la colección actual dado su identificador
   * Retorna null si no encuentra una coincidencia
   * @param shiftId identificador de un turno
   */
  public getShift(shiftId: number): IShift {
    if (this.collection) {
      return this.collection.find((shift: IShift) => shift.shiftId === shiftId);
    }
    return null;
  }

  private parseTimeString(timeString: any): { hours: number; minutes: number; seconds: number } {
    if (typeof timeString === 'string') {
      // Caso: `startTime` es un string en formato "HH:mm:ss"
      const [hours, minutes, seconds] = timeString.split(':').map(Number);
      return { hours, minutes, seconds };
    }
    if (typeof timeString === 'object' && timeString !== null) {
      // Caso: `startTime` es un objeto con las propiedades `hours`, `minutes` y `seconds`
      return {
        hours: timeString.hours || 0,
        minutes: timeString.minutes || 0,
        seconds: timeString.seconds || 0,
      };
    }
    throw new Error(`Invalid time format: ${JSON.stringify(timeString)}`);
  }

  public currentShift(): IShift {
    const collection = this.getShiftCollection();

    if (!collection) {
      throw { message: `collection_is_not_ready`, extra: this.name } as IError;
    }

    const now = moment(); // Hora actual
    const date = moment().format(environment.dateFormatSystem); // Fecha actual en formato adecuado
    const format = `${environment.dateFormatSystem} ${environment.timeFormat}`; // Formato completo

    const current = collection.find((shift) => {
      let hours: number;
      let minutes: number;
      let seconds: number;

      try {
        // Parsear `startTime` según sea string u objeto
        ({ hours, minutes, seconds } = this.parseTimeString(shift.startTime));
      } catch (error) {
        console.warn(`Skipping shift due to invalid startTime: ${shift.name}`, error);
        return false; // Ignorar turnos con formato inválido
      }

      // Calcular inicio y fin del turno
      const initDateTime = moment(`${date} ${padNumber(hours)}:${padNumber(minutes)}:${padNumber(seconds)}`, format);

      const finalDateTime = initDateTime.clone().add(shift.shiftSpan, 'hours').subtract(1, 'second');

      // Verificar si la hora actual está dentro del turno actual
      if (now.isBetween(initDateTime, finalDateTime)) {
        return true;
      }

      // Calcular inicio y fin del turno del día anterior
      const yesterdayInitDateTime = initDateTime.clone().subtract(1, 'day');
      const yesterdayFinalDateTime = finalDateTime.clone().subtract(1, 'day');

      if (now.isBetween(yesterdayInitDateTime, yesterdayFinalDateTime)) {
        return true;
      }

      return false;
    });

    if (current) {
      return current; // Retornar el turno actual encontrado
    }

    throw { message: `no_current_shift_found`, extra: this.name } as IError; // Lanzar error si no se encontró turno
  }

  /**
   * Retorna el siguiente turno
   */
  public nextShift(): IShift {
    if (this.collection) {
      const current = this.currentShift();
      let next: IShift;
      if (Array.isArray(this.collection)) {
        const maxlength = this.collection.length;
        if (current.shiftOrder === maxlength) {
          next = this.collection.find((x: IShift) => x.shiftOrder === 1);
        } else {
          next = this.collection.find((x: IShift) => x.shiftOrder === current.shiftOrder + 1);
        }
      }
      return next;
    }
    // TODO Error a config
    throw { message: `no_required_data_provided: ${this.name}` } as IError;
  }

  /**
   * Retorna el tiempo restante para el siguiente turno en milisegundos
   */
  public timeForNextShift(): number {
    const current = this.currentShift();
    const { hours, minutes, seconds } = this.parseTimeString(current.startTime);
    const currentInitShift = moment().startOf('day').add(hours, 'hour').add(minutes, 'minute').add(seconds, 'seconds');
    const nextShift = currentInitShift.add(current.shiftSpan, 'hours');
    const difference = nextShift.diff(moment()); // milliseconds
    return difference;
  }
}
